<template>
  <div class="lpr-dashboard p-2">
    <div class="lpr-dashboard__header">
      <h4 class="mb-0">{{ $t("grid.widgets_section.neo_dash.dashboard.title") }}</h4>
      <div class="lpr-dashboard__filters">
        <b-button-group size="sm" class="mr-1">
          <b-button
            v-for="r in ranges"
            :key="r.key"
            :variant="activeRange === r.key ? 'primary' : 'outline-primary'"
            @click="applyRange(r)"
          >
            {{ r.label }}
          </b-button>
        </b-button-group>
        <b-form-input v-model="from" type="date" size="sm" class="lpr-dashboard__date" @change="markCustomRange" />
        <span class="mx-1">—</span>
        <b-form-input v-model="to" type="date" size="sm" class="lpr-dashboard__date" @change="markCustomRange" />
        <b-button variant="primary" size="sm" class="ml-1" :disabled="loading" @click="fetch">
          {{ $t("actions.update") }}
        </b-button>
      </div>
    </div>

    <div v-if="loading" class="lpr-dashboard__state">
      <b-spinner small /> <span class="ml-1">{{ $t("actions.loading") }}…</span>
    </div>
    <div v-else-if="error" class="lpr-dashboard__state text-danger">
      {{ error }}
    </div>

    <template v-else-if="summary">
      <!-- KPI cards -->
      <b-row class="mt-1">
        <b-col cols="6" md="3" v-for="kpi in kpis" :key="kpi.label">
          <b-card class="lpr-kpi text-center mb-1">
            <div class="lpr-kpi__value">{{ kpi.value }}</div>
            <div class="lpr-kpi__label">{{ kpi.label }}</div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Charts -->
      <b-row>
        <b-col cols="12" md="6">
          <b-card :title="$t('grid.widgets_section.neo_dash.dashboard.by_day')" class="mb-1">
            <e-charts :key="`day-${renderKey}`" :options="byDayOption" autoresize class="lpr-chart" />
          </b-card>
        </b-col>
        <b-col cols="12" md="6">
          <b-card :title="$t('grid.widgets_section.neo_dash.dashboard.by_hour')" class="mb-1">
            <e-charts :key="`hour-${renderKey}`" :options="byHourOption" autoresize class="lpr-chart" />
          </b-card>
        </b-col>
        <b-col cols="12" md="6">
          <b-card :title="$t('grid.widgets_section.neo_dash.dashboard.by_color')" class="mb-1">
            <e-charts :key="`color-${renderKey}`" :options="byColorOption" autoresize class="lpr-chart" />
          </b-card>
        </b-col>
        <b-col cols="12" md="6">
          <b-card :title="$t('grid.widgets_section.neo_dash.dashboard.by_type')" class="mb-1">
            <e-charts :key="`type-${renderKey}`" :options="byTypeOption" autoresize class="lpr-chart" />
          </b-card>
        </b-col>
        <b-col cols="12" md="6">
          <b-card :title="$t('grid.widgets_section.neo_dash.dashboard.by_brand')" class="mb-1">
            <e-charts :key="`brand-${renderKey}`" :options="byBrandOption" autoresize class="lpr-chart" />
          </b-card>
        </b-col>
        <b-col cols="12" md="6">
          <b-card :title="$t('grid.widgets_section.neo_dash.dashboard.by_camera')" class="mb-1">
            <e-charts :key="`cam-${renderKey}`" :options="byCameraOption" autoresize class="lpr-chart" />
          </b-card>
        </b-col>
      </b-row>
    </template>
  </div>
</template>

<script>
import { BRow, BCol, BCard, BButton, BButtonGroup, BFormInput, BSpinner } from "bootstrap-vue";
import axios from "@axios";
import ECharts from "vue-echarts";
import "echarts/lib/chart/bar";
import "echarts/lib/chart/pie";
import "echarts/lib/component/tooltip";
import "echarts/lib/component/legend";
import "echarts/lib/component/grid";

import { COLOR_MAP, UNKNOWN_COLOR } from "./vehicleColors";

const toIsoDate = (date) => date.toISOString().slice(0, 10);

const ALL_TIME_FROM = "2000-01-01";

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BButtonGroup,
    BFormInput,
    BSpinner,
    ECharts,
  },
  props: {
    cameras: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      from: ALL_TIME_FROM,
      to: toIsoDate(new Date()),
      activeRange: "all",
      loading: false,
      error: null,
      summary: null,
      renderKey: 0,
    };
  },
  computed: {
    ranges() {
      return [
        { key: "all", label: this.$t("grid.widgets_section.neo_dash.dashboard.range_all"), days: null },
        { key: "365", label: "365d", days: 365 },
        { key: "90", label: "90d", days: 90 },
        { key: "30", label: "30d", days: 30 },
        { key: "7", label: "7d", days: 7 },
      ];
    },
    kpis() {
      const s = this.summary || {};
      return [
        { label: this.$t("grid.widgets_section.neo_dash.dashboard.total"), value: this.fmt(s.total) },
        { label: this.$t("grid.widgets_section.neo_dash.dashboard.plates"), value: this.fmt(s.distinct_plates) },
        { label: this.$t("grid.widgets_section.neo_dash.dashboard.peak_hour"), value: this.peakHourLabel },
        { label: this.$t("grid.widgets_section.neo_dash.dashboard.top_color"), value: this.topColorLabel },
      ];
    },
    peakHourLabel() {
      const h = this.summary ? this.summary.peak_hour : null;
      return h === null || h === undefined ? "—" : `${String(h).padStart(2, "0")}:00`;
    },
    topColorLabel() {
      const top = (this.summary?.by_color || []).find((c) => c.value && c.value !== "—");
      return top ? `${top.value} (${this.fmt(top.total)})` : "—";
    },
    byDayOption() {
      const rows = this.summary?.by_day || [];
      return this.barOption(rows.map((r) => r.date), rows.map((r) => r.total));
    },
    byHourOption() {
      const rows = this.summary?.by_hour || [];
      return this.barOption(
        rows.map((r) => `${String(r.hour).padStart(2, "0")}h`),
        rows.map((r) => r.total)
      );
    },
    byTypeOption() {
      const rows = this.summary?.by_type || [];
      return this.barOption(rows.map((r) => r.value), rows.map((r) => r.total), true);
    },
    byBrandOption() {
      const rows = this.summary?.by_brand || [];
      return this.barOption(rows.map((r) => r.value), rows.map((r) => r.total), true);
    },
    byCameraOption() {
      const rows = this.summary?.by_camera || [];
      return this.barOption(rows.map((r) => r.name), rows.map((r) => r.total));
    },
    byColorOption() {
      const rows = this.summary?.by_color || [];
      return {
        tooltip: { trigger: "item", formatter: "{b}: {c} ({d}%)" },
        legend: { bottom: 0, type: "scroll" },
        series: [
          {
            name: this.$t("grid.widgets_section.neo_dash.dashboard.by_color"),
            type: "pie",
            radius: ["45%", "70%"],
            avoidLabelOverlap: true,
            label: { show: false },
            data: rows.map((r) => {
              const color = COLOR_MAP[String(r.value || "").toLowerCase().trim()] || UNKNOWN_COLOR;
              const itemStyle = { color };

              if (color === "#FFFFFF") {
                itemStyle.borderColor = "#BDBDBD";
                itemStyle.borderWidth = 1;
              }
              return { name: r.value, value: r.total, itemStyle };
            }),
          },
        ],
      };
    },
  },
  mounted() {
    this.fetch();
  },
  methods: {
    fmt(n) {
      return (n ?? 0).toLocaleString();
    },
    applyRange(r) {
      this.activeRange = r.key;
      this.to = toIsoDate(new Date());
      if (r.days === null) {
        this.from = ALL_TIME_FROM;
      } else {
        const d = new Date();
        d.setDate(d.getDate() - r.days);
        this.from = toIsoDate(d);
      }
      this.fetch();
    },
    markCustomRange() {
      this.activeRange = null;
    },
    barOption(categories, values, horizontal = false) {
      const valueAxis = { type: "value" };
      const categoryAxis = { type: "category", data: categories };
      return {
        tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
        grid: { left: "3%", right: "4%", bottom: "3%", top: "12%", containLabel: true },
        xAxis: horizontal ? valueAxis : categoryAxis,
        yAxis: horizontal ? categoryAxis : valueAxis,
        series: [
          {
            name: this.$t("grid.widgets_section.neo_dash.dashboard.detections"),
            type: "bar",
            barMaxWidth: 40,
            data: values,
          },
        ],
      };
    },
    fetch() {
      this.loading = true;
      this.error = null;
      axios
        .get("/v1/detection_event/graph-analytics", {
          params: {
            from: `${this.from} 00:00:00`,
            to: `${this.to} 23:59:59`,
            cameras: this.cameras,
          },
        })
        .then(({ data }) => {
          this.summary = data.data;
          this.renderKey += 1;
        })
        .catch((e) => {
          this.error =
            e?.response?.data?.message ||
            this.$t("grid.widgets_section.neo_dash.dashboard.error");
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.lpr-dashboard {
  width: 100%;
  height: 100%;
  overflow-y: auto;
}

.lpr-dashboard__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.lpr-dashboard__filters {
  display: flex;
  align-items: center;
}

.lpr-dashboard__date {
  width: 150px;
}

.lpr-dashboard__state {
  display: flex;
  align-items: center;
  padding: 1rem 0;
}

.lpr-chart {
  width: 100%;
  height: 300px;
}

.lpr-kpi__value {
  font-size: 1.8rem;
  font-weight: 700;
  line-height: 1.1;
}

.lpr-kpi__label {
  font-size: 0.85rem;
  color: #6e6b7b;
}
</style>
